import { useTranslation } from 'react-i18next'
import { useCallback, useMemo } from 'react'
import { EmissionQualifyingParams, qpd23Schema } from '@/shared/api/EmissionQualifyingParamsModel'
import { UnitLabel } from '@/shared/config/config'
import { isValidPercentage, NumericInputGroup } from '@/common'
import QpdWithItemsEditor from './QpdWithItemsEditor'
import { useQpdWithItems } from './hooks/useQpdWithItems'

const MAX_OTHER_ELEMENTS = 4

export default function AluminiumQualifyingParamsForm({
  qualifyingParams,
  setQualifyingParams
}: {
  qualifyingParams: Partial<EmissionQualifyingParams>
  setQualifyingParams: (params: Partial<EmissionQualifyingParams>) => void
}) {
  const { t } = useTranslation()

  const onQpd23Change = useCallback(
    (value: number | null, items: { name: string; value: number }[]) => {
      setQualifyingParams({
        ...qualifyingParams,
        QPD23: { value, elements: items }
      })
    },
    [qualifyingParams, setQualifyingParams]
  )

  const { onNameChange, onValueChange, onRemoveOtherElement, onAddOtherElement } = useQpdWithItems({
    initialValue: {
      value: qualifyingParams.QPD23?.value || null,
      items: qualifyingParams.QPD23?.elements || []
    },
    maxItems: MAX_OTHER_ELEMENTS,
    setQualifyingParamValue: onQpd23Change
  })

  const qpd23Value = {
    value: qualifyingParams.QPD23?.value || null,
    elements: qualifyingParams.QPD23?.elements || []
  }

  const onNumericInputChange = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = evt.target
    setQualifyingParams({
      ...qualifyingParams,
      [name]: { value: value ? parseFloat(value) : null }
    })
  }

  const qpd21 = (
    <NumericInputGroup
      label={t('emissionQualifyingParams:fields.QPD21.label')}
      hint={t('emissionQualifyingParams:fields.QPD21.hint')}
      value={qualifyingParams.QPD21?.value?.toString() || ''}
      name="QPD21"
      onChange={onNumericInputChange}
      suffix={UnitLabel.tPerTAl}
      numericFormat={{
        decimalScale: 3,
        allowNegative: false
      }}
    />
  )

  const qpd22 = (
    <NumericInputGroup
      label={t('emissionQualifyingParams:fields.QPD22.label')}
      hint={t('emissionQualifyingParams:fields.QPD22.hint')}
      value={qualifyingParams.QPD22?.value?.toString() || ''}
      name="QPD22"
      onChange={onNumericInputChange}
      suffix={UnitLabel.percent}
      numericFormat={{
        decimalScale: 3,
        isAllowed: isValidPercentage
      }}
    />
  )

  const qpd23ValidationResult = useMemo(() => {
    return qpd23Schema.safeParse(qpd23Value)
  }, [qpd23Value])

  const hasQpd23RangeError = useMemo(() => {
    return (
      qpd23ValidationResult.error?.issues.some(
        issue =>
          (issue.code === 'too_small' || issue.code === 'too_big') && issue.path.includes('value')
      ) || false
    )
  }, [qpd23ValidationResult])

  return (
    <div className="space-y-4">
      <ul className="grid grid-cols-2 gap-4">
        <div className="space-y-2">{qpd21}</div>
        <div className="space-y-2">{qpd22}</div>
        <div className="space-y-2">
          <QpdWithItemsEditor
            strings={{
              itemPlaceholderName: t('emissionQualifyingParams:fields.QPD23.itemNamePlaceholder'),
              label: t('emissionQualifyingParams:fields.QPD23.label'),
              hint: t('emissionQualifyingParams:fields.QPD23.hint'),
              rangeError: t('emissionQualifyingParams:fields.QPD23.errors.rangeError'),
              instructions: t('emissionQualifyingParams:fields.QPD23.instructions')
            }}
            maxItems={MAX_OTHER_ELEMENTS}
            itemUnitSuffix={UnitLabel.percent}
            items={qpd23Value.elements}
            hasRangeError={hasQpd23RangeError}
            onNameChange={onNameChange}
            onValueChange={onValueChange}
            onRemoveOtherElement={onRemoveOtherElement}
            onAddOtherElement={onAddOtherElement}
          />
        </div>
      </ul>
    </div>
  )
}
